import React, { useState, useMemo } from 'react';

import {
  Panel,
  Waiting,
  Notification,
} from '@xbotvn/react-ui/components';
import {
  Button,
  Box,
  Tabs,
  Tab,
  Container,
  Icon,
} from '@xbotvn/react-ui/core';
import {
  cloneDeep,
  unset,
  set,
  uniq,
  getUniqueKey,
} from '@xbotvn/utils/collection';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { handleUpdateUnit } from '../../../redux/actions/user';
import Groups from './Groups';
import Group from './Groups/Group';
import Staffs from './Staffs';
import Staff from './Staffs/Staff';
import * as Styles from './styles';

function Unit({ onClose }) {
  const dispatch = useDispatch();
  const {
    staffs: initStaffs,
    admins: initAdmins,
    groups: initGroups,
    handling,
  } = useSelector(({ user }) => ({
    staffs: user?.unit?.staffs ?? [],
    admins: user?.unit?.admins ?? [],
    groups: user?.unit?.groups ?? {},
    handling: user?.handling ?? false,
  }));

  const [activeTab, setActiveTab] = useState('staffs');
  const [newStaff, setNewStaff] = useState(false);
  const [newGroup, setNewGroup] = useState(false);
  const [staffs, setStaffs] = useState(initStaffs);
  const [admins, setAdmins] = useState(initAdmins);
  const [groups, setGroups] = useState(initGroups);

  const action = useMemo(() => {
    const props = {
      color: 'secondary',
      style: { marginRight: 10 },
    };
    switch (activeTab) {
      case 'staffs':
        return (
          <Button
            {...props}
            onClick={() => setNewStaff(true)}
            startIcon={<Icon>personal_add</Icon>}
          >
            Thêm nhân sự
          </Button>
        );
      case 'groups':
        return (
          <Button
            {...props}
            onClick={() => setNewGroup(true)}
            startIcon={<Icon>group_add</Icon>}
          >
            Thêm nhóm
          </Button>
        );
      default:
        return null;
    }
  }, [activeTab]);

  const content = useMemo(() => {
    switch (activeTab) {
      case 'staffs':
        return (
          <Staffs
            staffs={staffs}
            admins={admins}
            onChange={(email, admin) => {
              if (admin === undefined) {
                setStaffs((prevStaffs) => prevStaffs.filter((e) => e !== email));
                setAdmins((prevAdmins) => prevAdmins.filter((e) => e !== email));
              } else {
                setAdmins((prevAdmins) => {
                  const newAdmins = prevAdmins.filter((e) => e !== email);
                  if (admin) newAdmins.push(email);
                  return newAdmins;
                });
              }
            }}
          />
        );
      case 'groups':
        return (
          <Groups
            staffs={staffs}
            groups={groups}
            onChange={(gid, name, emails) => {
              setGroups((prevGroups) => {
                const cloned = cloneDeep(prevGroups);
                if (!name) unset(cloned, gid);
                else set(cloned, gid, ({ name, staffs: emails || [] }));
                return cloned;
              });
            }}
          />
        );
      default:
        return null;
    }
  }, [
    activeTab,
    staffs,
    admins,
    groups,
  ]);

  return (
    <Panel
      anchor="right"
      open
      onClose={onClose}
      title="Nhân sự"
      actions={(
        <Box display="flex" justifyContent="flex-end" width={1}>
          {action}
          <Button
            color="primary"
            onClick={() => {
              dispatch(handleUpdateUnit(
                staffs,
                admins,
                groups,
                {},
                (err) => {
                  if (!err) onClose();
                },
              ));
            }}
            startIcon={<Icon>save</Icon>}
          >
            Cập nhật
          </Button>
        </Box>
      )}
    >
      {handling ? <Waiting fullscreen /> : null}
      {newStaff ? (
        <Staff
          onClose={(email, admin) => {
            if (email) {
              setStaffs((prevStaffs) => {
                if (prevStaffs.includes(email)) {
                  Notification.warn('Tài khoản này đã được thêm.');
                  return prevStaffs;
                }
                return [email, ...prevStaffs];
              });
              if (admin) setAdmins((prevAdmins) => uniq([...prevAdmins, email]));
            }
            setNewStaff();
          }}
        />
      ) : null}
      {newGroup ? (
        <Group
          staffs={staffs}
          onClose={(name, members = []) => {
            if (name) {
              setGroups((prevGroups) => ({
                ...prevGroups,
                [getUniqueKey(Object.keys(prevGroups))]: {
                  name,
                  staffs: members.split(', '),
                },
              }));
            }
            setNewGroup();
          }}
        />
      ) : null}
      <Box width={650}>
        <Styles.UnitContainer>
          <Tabs
            value={activeTab}
            onChange={(e, value) => setActiveTab(value)}
            textColor="primary"
            centered
          >
            <Tab label="Nhân sự" value="staffs" />
            <Tab label="Nhóm" value="groups" />
          </Tabs>
          <Container style={{ marginTop: 20 }}>
            {content}
          </Container>
        </Styles.UnitContainer>
      </Box>
    </Panel>
  );
}

Unit.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default Unit;
